/**
 * EventConfigStep — Step 3 of the Rodeo Wizard
 *
 * Manages per-event-type configuration:
 * event type, category (timed/scored), fees, contestant limit,
 * non-member surcharge, and performance assignment.
 *
 * Add/edit is driven by the shared RodeoEventDialog — same component the
 * My Rodeos expanded list uses, so the add/edit UX matches across surfaces.
 */

import { useState } from 'react';
import { Plus, Trash2, Timer, Target, Pencil, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { useRodeoStore } from '@/state_management_and_stores/rodeo.store';
import {
  RodeoEventDialog,
  type RodeoEventDialogValues,
} from '@/components/rodeo-wizard/RodeoEventDialog';
import { getEventDisplayTitle } from '@/utils/eventTypes';

export function EventConfigStep() {
  const events = useRodeoStore((s) => s.events);
  const performances = useRodeoStore((s) => s.performances);
  // Org that owns this rodeo — drives the inherited org-default prize
  // adjustments shown in the event dialog's Prize Adjustments section.
  const organizationId = useRodeoStore((s) => s.basicInfo.organization_id);
  const addEvent = useRodeoStore((s) => s.addEvent);
  const updateEvent = useRodeoStore((s) => s.updateEvent);
  const removeEvent = useRodeoStore((s) => s.removeEvent);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);

  const editingConfig = editingKey
    ? events.find((c) => c._key === editingKey)
    : undefined;

  const perfName = (key: string) =>
    performances.find((p) => p._key === key)?.performance_name ?? 'Unassigned';

  const handleAddClick = () => {
    setEditingKey(null);
    setDialogOpen(true);
  };

  const handleEditClick = (key: string) => {
    setEditingKey(key);
    setDialogOpen(true);
  };

  const handleSubmit = (values: RodeoEventDialogValues) => {
    // Fold dialog-shape rounds into RoundConfigDraft. Each round still runs in
    // exactly one perf: the dialog's per-round Performance picker when the user
    // chose one, otherwise the event's first assigned perf. The Avg row is a
    // payout aggregate rather than a run, so it stays unassigned.
    const defaultPerfKey =
      values.performance_assignments[0]?.performance_key ?? '';
    const round_configs = values.rounds.map((r, i) => ({
      _key:
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `round-${Date.now()}-${i}`,
      round_number: r.round_number,
      round_name: r.round_name,
      performance_key: r.is_average ? '' : (r.performance_key || defaultPerfKey),
      is_go_round: r.is_go_round,
      is_short_round: r.is_short_round,
      advancing_count: r.advancing_count,
      is_average: !!r.is_average,
      has_prizes: r.has_prizes,
      round_prize_total: r.round_prize_total,
      is_progressive: r.is_progressive,
      places: r.places,
    }));
    const { rounds: _rounds, ...rest } = values;
    const payload = { ...rest, round_configs };
    if (editingKey) {
      updateEvent(editingKey, payload);
    } else {
      addEvent(payload);
    }
    setDialogOpen(false);
    setEditingKey(null);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Events</h2>
          <p className="text-sm text-muted-foreground">
            Configure each event with fees, limits, and scoring category.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={handleAddClick}>
          <Plus className="h-4 w-4 mr-1" /> Add Event
        </Button>
      </div>

      {/* Event config list */}
      {events.length === 0 ? (
        <EmptyState
          icon={Target}
          title="No events"
          description="Add events with their fees, scoring, and contestant limits."
        />
      ) : (
        <div className="space-y-2">
          {events.map((ec) => {
            const pending = !!ec._pendingDelete;
            const pendingEdit = !pending && !!ec._pendingEdit;
            return (
              <div
                key={ec._key}
                className={`flex items-center gap-3 border rounded-md px-3 py-2.5 ${
                  pending
                    ? 'opacity-60 border-destructive/40 bg-destructive/5'
                    : pendingEdit
                      ? 'border-amber-400/60 bg-amber-500/5'
                      : ''
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`font-medium text-sm truncate ${pending ? 'line-through' : ''}`}>
                      {getEventDisplayTitle({
                        event_name: ec.event_name,
                        event_type: ec.event_type,
                      })}
                    </p>
                    <Badge
                      variant="outline"
                      className={`text-xs ${ec.event_category === 'scored' ? 'text-amber-600 border-amber-300' : 'text-blue-600 border-blue-300'}`}
                    >
                      {ec.event_category === 'scored' ? (
                        <Target className="h-3 w-3 mr-0.5" />
                      ) : (
                        <Timer className="h-3 w-3 mr-0.5" />
                      )}
                      {ec.event_category}
                    </Badge>
                    {/* Performance assignments — one chip per perf. Unassigned
                        events show a dashed amber warning chip so the missing
                        assignment jumps out at a glance. */}
                    {ec.performance_assignments.length > 0 ? (
                      ec.performance_assignments.map((a) => (
                        <Badge
                          key={a.performance_key}
                          variant="outline"
                          className="text-xs border-purple-400/60 text-purple-700 dark:text-purple-300 bg-purple-500/10"
                          title={
                            a.max_contestants != null
                              ? `Cap ${a.max_contestants} (override)`
                              : undefined
                          }
                        >
                          {perfName(a.performance_key)}
                          {a.max_contestants != null && (
                            <span className="ml-1 text-[10px] opacity-75">
                              ·{a.max_contestants}
                            </span>
                          )}
                        </Badge>
                      ))
                    ) : (
                      <Badge
                        variant="outline"
                        className="text-xs border-dashed border-amber-500/70 text-amber-700 dark:text-amber-300 bg-amber-500/10"
                      >
                        Unassigned
                      </Badge>
                    )}
                    {pending && (
                      <Badge variant="outline" className="text-xs text-destructive border-destructive/60">
                        Pending delete
                      </Badge>
                    )}
                    {pendingEdit && (
                      <Badge variant="outline" className="text-xs text-amber-600 border-amber-400/70">
                        Unsaved changes
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {ec.entry_fee != null ? `$${ec.entry_fee} entry` : 'No entry fee'}
                    {ec.transaction_fee != null ? ` + $${ec.transaction_fee} txn` : ''}
                    {typeof ec.contestant_limit === 'number' && ec.contestant_limit > 0
                      ? ` \u00b7 Max ${ec.contestant_limit}`
                      : ''}
                    {typeof ec.contestants_per_performance === 'number' &&
                    ec.contestants_per_performance > 0
                      ? ` \u00b7 ${ec.contestants_per_performance}/perf`
                      : ''}
                    {ec.round_configs.length > 0
                      ? ` \u00b7 ${ec.round_configs.length} round${ec.round_configs.length !== 1 ? 's' : ''}`
                      : ''}
                    {ec.total_prize_amount != null && ec.total_prize_amount > 0
                      ? ` \u00b7 $${ec.total_prize_amount} prize`
                      : ''}
                    {pending ? ' \u00b7 Will be removed on Save Draft' : ''}
                    {pendingEdit ? ' \u00b7 Will be saved on Save Draft' : ''}
                  </p>
                </div>
                {!pending && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={() => handleEditClick(ec._key)}
                    aria-label="Edit event"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className={`h-7 w-7 shrink-0 ${
                    pending ? '' : 'text-destructive hover:text-destructive'
                  }`}
                  onClick={() => removeEvent(ec._key)}
                  aria-label={pending ? 'Undo delete' : 'Delete event'}
                  title={pending ? 'Undo delete' : 'Delete event'}
                >
                  {pending ? (
                    <Undo2 className="h-3.5 w-3.5" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5" />
                  )}
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <RodeoEventDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingKey(null);
        }}
        mode={editingKey ? 'edit' : 'add'}
        initialValues={
          editingConfig
            ? {
                event_name: editingConfig.event_name ?? '',
                event_type: editingConfig.event_type,
                event_category: editingConfig.event_category,
                entry_fee: editingConfig.entry_fee,
                transaction_fee: editingConfig.transaction_fee,
                contestant_limit: editingConfig.contestant_limit,
                contestants_per_performance:
                  (editingConfig as any).contestants_per_performance ?? null,
                non_member_surcharge: editingConfig.non_member_surcharge,
                allow_multiple_registrations:
                  !!editingConfig.allow_multiple_registrations,
                max_registrations_per_user:
                  editingConfig.max_registrations_per_user ?? null,
                enforce_balanced_positions:
                  !!editingConfig.enforce_balanced_positions,
                // Tri-state — preserve null (inherit the rodeo's Pay Later
                // switch) rather than coercing it to a boolean.
                allow_pay_later: editingConfig.allow_pay_later ?? null,
                performance_assignments: editingConfig.performance_assignments,
                rounds: editingConfig.round_configs.map((r) => ({
                  round_number: r.round_number,
                  round_name: r.round_name,
                  is_go_round: r.is_go_round,
                  is_short_round: r.is_short_round,
                  advancing_count: r.advancing_count,
                  is_average: !!r.is_average,
                  // '' in the store means "unassigned" — map it back to the
                  // dialog's null ("Default") rather than an empty Select value.
                  performance_key: r.performance_key || null,
                  has_prizes: !!r.has_prizes,
                  round_prize_total: Number(r.round_prize_total) || 0,
                  is_progressive: !!r.is_progressive,
                  places: Array.isArray(r.places) ? r.places : [],
                })),
                total_prize_amount: editingConfig.total_prize_amount ?? null,
                prize_adjustments: editingConfig.prize_adjustments ?? [],
                prize_allocation_mode:
                  editingConfig.prize_allocation_mode === 'dynamic' ? 'dynamic' : 'fixed',
                entry_fee_prize_portion: editingConfig.entry_fee_prize_portion ?? null,
                entry_fee_prize_portion_is_percentage:
                  !!editingConfig.entry_fee_prize_portion_is_percentage,
                entry_fee_prize_portion_is_fee_deduction:
                  !!editingConfig.entry_fee_prize_portion_is_fee_deduction,
                display_prize_to_contestants: !!editingConfig.display_prize_to_contestants,
                show_prize_amounts_in_results:
                  editingConfig.show_prize_amounts_in_results !== false,
                show_prize_amounts_before_complete:
                  !!editingConfig.show_prize_amounts_before_complete,
                default_adjustment_overrides:
                  editingConfig.default_adjustment_overrides ?? {},
              }
            : undefined
        }
        organizationId={organizationId || null}
        performances={performances
          .filter((p) => !p._pendingDelete)
          .map((p) => ({
            key: p._key,
            name: p.performance_name,
            performance_date: p.performance_date,
            start_time: p.start_time,
            // Event types already claiming this perf (excluding the event
            // being edited — editing our own row shouldn't self-warn). Drives
            // the chips shown next to each perf toggle so the user can see
            // conflicts before checking the switch.
            assigned_event_types: events
              .filter(
                (ec) =>
                  !ec._pendingDelete &&
                  (editingKey ? ec._key !== editingKey : true) &&
                  ec.performance_assignments.some(
                    (a) => a.performance_key === p._key,
                  ),
              )
              .map((ec) => ec.event_type),
            default_max_contestants: p.default_max_contestants,
          }))}
        usedEventTypes={events
          .filter(
            (ec) =>
              !ec._pendingDelete && (editingKey ? ec._key !== editingKey : true),
          )
          .map((ec) => ec.event_type)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
